import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../../abstract/api.service';
import { Profile } from '../data-models/profile.model';
import { ProfileRequest } from '../request-models/profile-request.model';
import { RegisterWithOrganizationRequest } from '../request-models/register-with-organization-request.model';
import { CurrentUserResponse } from '../response-models/current-user-response.model';
import { LoginFailedResponse } from '../response-models/login-failed-response.model';
import { RegisterResponse } from '../response-models/register-response.model';
import { RegisterWithOrganizationResponse } from '../response-models/register-with-organization-response.model';
import { ResetActionCodeResponse } from '../response-models/reset-action-code-response.model';
import { SyncProfileResponse } from '../response-models/sync-profile-response.model';
import { isRecord } from '../utils/is-record';
import { nullableBoolean, nullableNumber, nullableString } from '../utils/wire-value-utils';
import { toProfileRequest } from '../utils/profile-request-utils';

type Method = 'GET' | 'POST' | 'PUT';

/**
 * The hub's `/auth` routes (`auth-controller.ts`): registration, the signed-in user's profile, login
 * bookkeeping and action codes. Its sub-resources have their own services: `AuthEmailCodeService`
 * (`/auth/email-codes`) and `AuthInvitationService` (`/auth/invitations`).
 * `/auth` is not an entity resource (it is mostly actions), so this is an api-service: every call goes
 * through `request()`, and the profile routes build a `Profile` from the hub's data.
 * Failures reject with an `ApiError` (`message` is the hub's error, `code` its typed error code).
 */
@Injectable({
  providedIn: 'root'
})
export class AuthApiService extends ApiService {
  constructor() {
    super('auth');
  }

  /** Register a new user (Firebase user and profile). */
  async register(email: string, password: string, firstName: string, lastName: string): Promise<RegisterResponse> {
    const read = (data: unknown) => ({ userId: stringField(data, 'userId') });
    return this.call('POST', 'register', read, { email, password, firstName, lastName });
  }

  /** Create the profile for a newly created Firebase user (a no-op when it already exists). */
  async syncProfile(): Promise<SyncProfileResponse> {
    const read = (data: unknown) => ({
      userId: stringField(data, 'userId'),
      profileCreated: isRecord(data) && nullableBoolean(data['profileCreated']) === true,
    });
    return this.call('POST', 'sync-profile', read, {});
  }

  /**
   * Register the signed-in Firebase user with a new organization in a single hub transaction.
   * Mirrors the mobile signup path (CropSwipe → `POST /auth/register-with-organization`).
   */
  async registerWithOrganization(body: RegisterWithOrganizationRequest): Promise<RegisterWithOrganizationResponse> {
    const read = (data: unknown) => ({
      profileId: stringField(data, 'profileId'),
      organizationId: stringField(data, 'organizationId'),
      customClaims: isRecord(data) ? data['customClaims'] : undefined,
    });
    return this.call('POST', 'register-with-organization', read, body);
  }

  /**
   * Record a completed login. Web auth happens client-side via Firebase, so this lets the hub stamp
   * lastLogin / lastLoginLocation.
   */
  async recordLogin(): Promise<void> {
    return this.call('POST', 'record-login', () => undefined, {});
  }

  /** Record a failed login; the result says whether the account is now locked. */
  async recordFailedLoginAttempt(email: string): Promise<LoginFailedResponse> {
    const read = (data: unknown) => ({ locked: isRecord(data) && nullableBoolean(data['locked']) === true });
    return this.call('POST', 'login-failed', read, { email });
  }

  /** Tell the hub an email address was verified in Firebase. */
  async verifyEmail(email: string): Promise<void> {
    return this.call('POST', 'sync-email-verified', () => undefined, { email });
  }

  /** The signed-in user's profile, claims and memberships: `GET /auth/me`. */
  async getCurrentUser(): Promise<CurrentUserResponse> {
    return this.call('GET', 'me', data => this.toCurrentUser(data));
  }

  /** Update the signed-in user's profile. Sends only the fields that are set. */
  async updateProfile(profileData: ProfileRequest): Promise<Profile | null> {
    const read = (data: unknown) => (isRecord(data) ? new Profile(data) : null);
    return this.call('PUT', 'profile', read, toProfileRequest(profileData));
  }

  /** A password-reset `oobCode` for the signed-in user. */
  async getResetActionCode(): Promise<ResetActionCodeResponse> {
    return this.call('POST', 'action-codes/reset', data => ({ oobCode: stringField(data, 'oobCode') }), {});
  }

  /**
   * Coordinates for an address: `GET /auth/geocode`. `null` when the hub returns no usable pair.
   * An address the hub can't place rejects with an `ApiError` (status 422).
   */
  async geocode(address: string, city: string, state: string, zipCode: string): Promise<{ lat: number; lng: number } | null> {
    const read = (data: unknown) => toLatLng(data);
    return firstValueFrom(this.request('GET', { path: 'geocode', query: { address, city, state, zipCode } }, read));
  }

  private call<R>(method: Method, path: string, read: (data: unknown) => R, body?: unknown): Promise<R> {
    return firstValueFrom(this.request(method, { path }, read, body));
  }

  private toCurrentUser(data: unknown): CurrentUserResponse {
    const source = isRecord(data) ? data : {};
    const memberships = Array.isArray(source['memberships']) ? source['memberships'] : [];
    return {
      profile: isRecord(source['profile']) ? new Profile(source['profile']) : null,
      customClaims: source['customClaims'],
      memberships: memberships as CurrentUserResponse['memberships'],
    };
  }
}

/** `{ lat, lng }` from the hub's `{ latitude, longitude }`, keeping zeros; `null` unless both are numbers. */
function toLatLng(data: unknown): { lat: number; lng: number } | null {
  const lat = isRecord(data) ? nullableNumber(data['latitude']) : undefined;
  const lng = isRecord(data) ? nullableNumber(data['longitude']) : undefined;
  return lat != null && lng != null ? { lat, lng } : null;
}

function stringField(data: unknown, key: string): string {
  return (isRecord(data) ? nullableString(data[key]) : undefined) ?? '';
}
